<script setup>
import { Link, router } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import Pagination from '@/Components/Pagination.vue';
import { ref, watch, onMounted } from "vue";
import debounce from "lodash/debounce";
import { useToast } from 'vue-toast-notification';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import Loading from 'vue-loading-overlay';

const props = defineProps({
    year: String,
    agents: Object,
    filters: Object,
    flash: Object,
    user: Object,
});

let agentSearch = ref(props.filters.agentSearch);
const $toast = useToast();
dayjs.extend(advancedFormat);
let isLoading = ref(false);

watch(agentSearch, debounce(function (value) {
    router.get('/agents', { agentSearch: value }, {
        preserveState: true,
        replace: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
    });
}, 700));

onMounted(() => {
    if (props.flash.message) {
        $toast.open({
            message: props.flash.message,
            type: "info",
            duration: 4000,
            position: 'top-right',
            class: 'bs-toast toast bg-dark show'
        });
    }
});

const getInitials = (name) => {
    let rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu');
    let initials = [...name.matchAll(rgx)] || [];
    initials = (
        (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
    ).toUpperCase();
    return initials;
};
</script>
<style scoped>
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
@import "vue-loading-overlay/dist/vue-loading.css";
</style>
<template>
    <AppLayout title="Agent Manager" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="card">
                <div class="card-datatable table-responsive">
                    <div id="DataTables_Table_0_wrapper" class="dataTables_wrapper dt-bootstrap5 no-footer">
                        <div class="card-header flex-column flex-md-row">
                            <div class="head-label text-center">
                                <h5 class="card-title mb-0">Agents Manager</h5>
                            </div>
                            <div class="dt-action-buttons text-end pt-3 pt-md-0">
                                <div class="dt-buttons" v-if="$page.props.user.permissions.includes('add-agents')">
                                    <Link type="button" class="btn btn-primary" href="/agents/create/" preserve-scroll>
                                    <i class='bx bx-plus bx-tada'></i> Add New Agent
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-sm-12 col-md-12 d-flex justify-content-center justify-content-md-end">
                                <div class="dataTables_filter">
                                    <label>Search:
                                        <input v-model="agentSearch" type="text" class="form-control"
                                            placeholder="Type Agency Name"></label>
                                </div>
                            </div>
                        </div>
                        <table class="datatables-basic table border-top dataTable no-footer dtr-column"
                            id="DataTables_Table_0" role="grid" aria-describedby="DataTables_Table_0_info"
                            style="width: 1390px;">
                            <thead>
                                <tr role="row">
                                    <th>Name</th>
                                    <th>Country</th>
                                    <th>Contact</th>
                                    <th>Email</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="odd" v-for="agent in agents.data">
                                    <td>
                                        <div class="d-flex justify-content-start align-items-center user-name">
                                            <div class="avatar-wrapper">
                                                <div class="avatar me-2"><span
                                                        class="avatar-initial rounded-circle bg-label-warning">{{
                                                            getInitials(agent.agency_name)
                                                        }}</span>
                                                </div>
                                            </div>
                                            <div class=" d-flex flex-column">
                                                <span class="emp_name text-truncate">{{ agent.agency_name }}</span>
                                                <small class="emp_post text-truncate text-muted">
                                                    {{ agent.city?.name }}, {{ agent.country?.name }}
                                                </small>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        {{ agent.country?.name }}
                                    </td>
                                    <td>{{ agent.agency_mobile }}
                                    </td>
                                    <td>{{ agent.agency_email }}</td>
                                    <td>
                                        <span v-if="agent.active == 1" class="badge bg-label-success">Active</span>
                                        <span v-else class="badge bg-label-danger">Inactive</span>
                                    </td>
                                    <td>
                                        <Link :href="'/agents/show/' + agent.id"
                                            v-if="$page.props.user.permissions.includes('view-agents')"
                                            class="btn btn-icon btn-label-secondary" preserve-state>
                                        <i class="bx bx-search-alt"></i>
                                        </Link>
                                        <Link :href="'/agents/edit/' + agent.id"
                                            v-if="$page.props.user.permissions.includes('edit-agents')"
                                            class="btn btn-icon btn-label-secondary m-1" preserve-state>
                                        <i class="bx bx-edit"></i>
                                        </Link>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="row">
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_info" id="DataTables_Table_0_info" role="status"
                                    aria-live="polite">Showing {{ agents.from }} to {{ agents.to }} of {{
                                        agents.total
                                    }} Bookings</div>
                            </div>
                            <div class="col-sm-12 col-md-6">
                                <div class="dataTables_paginate">
                                    <Pagination :links="agents.links"></Pagination>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>